import { onBeforeUpdate as _onBeforeUpdate2 } from "gyron";
import { onBeforeUpdate as _onBeforeUpdate } from "gyron";
import { h as _h } from "gyron";
import { FC, onAfterMount, useMemo, useValue } from 'gyron';
import { Link, useRouter } from '@gyron/router';
import { DarkToggle } from '@/components/dark';
import { CloseIcon, GithubIcon, LogoIcon, MenuIcon } from '@/components/icons';
import { Nav } from './nav';
import { DOCS_NAV, CORE_NAV, BLOG_NAV, CORE_NAV_EN, BLOG_NAV_EN, DOCS_NAV_EN } from '@/pages';
import { isUesLightTheme } from '@/hooks/light';
import { TogglerTranslate } from './translate';
import { $t, resolveTranslate } from '@/langs';
import pkg from '../../../package.json';
import classnames from 'classnames';
import docsearch from '@docsearch/js';
import '@docsearch/css';
const Portal = FC(({
  children,
  isSSR
}) => {
  // Auxiliary update of deconstructed props
  _onBeforeUpdate((_, props) => {
    var _props = props;
    children = _props.children;
    isSSR = _props.isSSR;
  })
  if (!isSSR) {
    onAfterMount(component => {
      if (component.vnode.el) {
        document.body.appendChild(component.vnode.el);
      }
    });
  }
  return children;
});
export const Header = FC(({
  lang
}) => {
  // Auxiliary update of deconstructed props
  _onBeforeUpdate((_, props) => {
    var _props2 = props;
    lang = _props2.lang;
  })
  const router = useRouter();
  const expand = useValue(false);
  const menus = useMemo(() => {
    const path = router.path;
    if (path.startsWith('/en-US')) {
      return path.startsWith('/en-US/core') ? CORE_NAV_EN : path.startsWith('/en-US/blog') ? BLOG_NAV_EN : DOCS_NAV_EN;
    }
    return path.startsWith('/core') ? CORE_NAV : path.startsWith('/blog') ? BLOG_NAV : DOCS_NAV;
  });
  function onToggleExpand() {
    expand.value = !expand.value;
    if (expand.value) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.removeAttribute('style');
    }
  }
  onAfterMount(() => {
    docsearch({
      container: '#docsearch',
      appId: '8M4INMGM9P',
      indexName: 'gyron',
      apiKey: 'e33f7babfe95749aae99870e61e9b7a1'
    });
  });
  return ({
    lang
  }) => _h("header", {
    "class": classnames('sticky top-0 z-50 h-[58px] backdrop-blur py-4 w-full px-4 md:px-8 transition-colors duration-500 lg:z-50 bg-opacity-60 dark:bg-slate-900/75')
  }, _h("div", {
    "class": classnames('lg:m-auto lg:max-w-[86rem] pb-4', 'border-b border-solid border-gray-300/30 dark:border-gray-700 lg:border-b lg:border-slate-300/20 dark:border-slate-50/[0.06]', 'flex items-center justify-between sm-down:max-w-none dark:text-slate-200', {
      'text-slate-200': isUesLightTheme(router.path),
      'border-none': ['/', '/en-US', '/explorer', '/en-US/explorer'].includes(router.path)
    })
  }, [_h(Link, {
    "to": resolveTranslate('/', lang),
    "activeClassName": "nav-active-link",
    "className": classnames('hidden md:block overflow-hidden w-0 h-0 md:w-auto md:h-auto items-center', {
      'text-primary-400 dark:text-slate-200': !isUesLightTheme(router.path)
    })
  }, _h("span", {
    "class": "flex items-center"
  }, [_h(LogoIcon, {}, []), _h("span", {
    "class": "ml-3 text-sm"
  }, "Gyron.js"), _h("span", {
    "class": "ml-3 text-xs"
  }, ["v", pkg.version])])), _h("div", {
    "class": "md:hidden"
  }, [_h(MenuIcon, {
    "class": classnames({
      hidden: router.path === '/'
    }),
    "expand": onToggleExpand
  }, []), _h(Portal, {}, _h("span", {}, [_h("div", {
    "class": classnames('fixed inset-0 z-50 bg-black/20 backdrop-blur-sm bg-opacity-60 dark:bg-slate-900/80 w-0 h-[100vh] opacity-0 transition-opacity', {
      'w-[100vw] opacity-100': expand.value
    }),
    "aria-hidden": "true"
  }, []), _h("div", {
    "class": classnames('fixed inset-0 z-50 lg:hidden transition-all w-0 translate-x-0 h-[calc(100vh-env(safe-area-inset-bottom))]', {
      'w-[100vw]': expand.value
    }),
    "aria-modal": "true",
    "role": "dialog",
    "onClick": onToggleExpand
  }, _h("div", {
    "class": classnames('relative bg-white w-80 max-w-[calc(100%-3rem)] dark:bg-slate-800 h-full overflow-y-auto transition-all pt-10', {
      'p-6': expand.value
    }),
    "onClick": e => e.stopPropagation()
  }, [_h("div", {
    "class": "absolute right-6 w-6 h-6 flex items-center justify-center",
    "onClick": onToggleExpand
  }, _h(CloseIcon, {
    "class": "w-2.5 h-2.5"
  }, [])), _h(Nav, {
    "menus": menus.value,
    "changed": onToggleExpand
  }, [])]))]))]), _h("nav", {
    "class": classnames('flex flex-none', {
      'text-slate-200': router.path === '/'
    })
  }, [_h("ul", {
    "class": "font-bold text-sm md:text-base flex sm-down:space-y-6 space-x-5 md:space-x-6 items-center"
  }, [_h("li", {}, _h(Link, {
    "to": resolveTranslate('/docs/tutorial', lang),
    "activeClassName": "nav-active-link"
  }, $t('JSXText_151_74_153_14', lang))), _h("li", {}, _h(Link, {
    "to": resolveTranslate('/core', lang),
    "activeClassName": "nav-active-link"
  }, $t('JSXText_156_65_158_14', lang))), _h("li", {}, _h(Link, {
    "to": resolveTranslate('/blog/readme', lang),
    "activeClassName": "nav-active-link"
  }, $t('JSXText_161_72_163_14', lang))), _h("li", {}, _h(Link, {
    "to": resolveTranslate('/explorer', lang),
    "activeClassName": "nav-active-link"
  }, $t('JSXText_166_69_168_14', lang)))]), _h("div", {
    "class": "flex items-center border-l border-slate-200 ml-3 md:ml-6 pl-3 md:pl-6 dark:border-slate-800 space-x-5 md:space-x-6"
  }, [_h(DarkToggle, {}, []), _h("a", {
    "href": "https://github.com/gyronorg/core",
    "target": "_blank",
    "title": $t('JSXAttribute_176_14_176_26', lang)
  }, [_h("span", {
    "class": "hidden"
  }, "Github"), _h(GithubIcon, {}, [])]), _h(TogglerTranslate, {}, [])]), _h("div", {
    "class": classnames('w-15 sm:w-[199px] doc-light-search', {
      'doc-home-search': isUesLightTheme(router.path)
    })
  }, _h("div", {
    "id": "docsearch",
    "class": "pointer-events-auto border-l border-slate-200 dark:border-slate-800 ml-3 md:ml-6 pl-3 md:pl-6"
  }, []))])]));
});